import { useCallback, useMemo, useRef, useState } from 'react';
import { Message } from '@/types';
import * as conversationService from '@/services/conversationService';
import { useConversationsContext } from '@/contexts/ConversationsContext';
import { useConversationsStore, useUIStore } from '@/stores';
import { isPendingId } from '@/utils/messageUtils';
// import { fetchWithAuth } from '@/lib/utils';
/**
 * useConversations Hook
 * 管理会话列表和当前会话的状态 (使用 Zustand Store)
 */
export function useConversations(projectId?: string | null) {
  // 从 Zustand store 获取 conversations
  const conversations = useConversationsStore(state => state.conversations);
  const setConversations = useConversationsStore(
    state => state.setConversations
  );
  const addMessageToConversation = useConversationsStore(
    state => state.addMessageToConversation
  );
  // 注意：selectedAgent 通过 useUIStore.getState() 获取，避免成为依赖项
  const { setSelectedAgent, setAgentConfigValues } = useUIStore();
  // 生成当前 projectId 的 loading key
  const loadingKey = projectId || 'general';
  const isLoading = useConversationsStore(
    state => state.loadingStates[loadingKey] || false
  );
  const setIsLoading = useConversationsStore(state => state.setLoadingState);
  const {
    currentConversationId,
    setCurrentConversationId,
    expandedProjectIds,
    sidebarProjectConversationsCache,
    setSidebarProjectConversationsCache,
    projectExpand,
  } = useConversationsContext();
  // 添加 pendingConversationId 状态，用于在加载详情时显示 loading
  const [pendingConversationId, setPendingConversationId] = useState<
    string | undefined
  >(undefined);
  // 使用 ref 防止重复调用
  const loadingConversationsRef = useRef(false);
  const loadingDetailsRef = useRef<Set<string>>(new Set());
  const conversationsLoadedRef = useRef(false);
  // 使用 ref 立即跟踪目标 conversationId，避免异步状态更新导致的重复调用
  const targetConversationIdRef = useRef<string | undefined>(undefined);
  // 跟踪上一个 projectId，当 projectId 变化时重置加载状态
  const prevProjectIdRef = useRef(projectId);
  /**
   * 根据 client.id 加载并设置 agent 配置
   * forceResetValues: 是否强制重置配置值（即使 agent 相同）
   */
  const loadAgentConfig = useCallback(
    async (
      clientId: string,
      clientName?: string,
      clientLogo?: string,
      forceResetValues: boolean = false
    ) => {
      try {
        // 🔥 性能优化：使用 store.getState() 获取最新 selectedAgent，避免依赖
        const currentSelectedAgent = useUIStore.getState().selectedAgent;
        // 如果 agent 已经选中且有 config，直接使用缓存的 config
        // 但如果 forceResetValues 为 true，仍然需要重置配置值
        if (
          currentSelectedAgent?.id === clientId &&
          currentSelectedAgent?.config &&
          !forceResetValues
        ) {
          return;
        }
        let agentConfig = currentSelectedAgent?.config;
        let agentData: any = null;
        // 如果 agent 不同，或者没有缓存的 config，则需要重新加载
        // if (selectedAgent?.id !== clientId || !selectedAgent?.config) {
        //   // 调用 API 获取 agent 配置
        //   const response = await fetchWithAuth(
        //     `/api/clients/${clientId}/config`
        //   );
        //   if (!response.ok) {
        //     throw new Error(`Failed to fetch agent config: ${response.status}`);
        //   }
        //   agentData = await response.json();
        //   agentConfig = agentData.config || [];
        // }
        // 根据 config 数组设置默认的 agentConfigValues
        agentConfig = [];
        const defaultConfigValues: Record<string, any> = {};
        // if (agentConfig && Array.isArray(agentConfig)) {
        //   agentConfig.forEach((item: any, index: number) => {
        //     const key = item.name || `field_${index}`;
        //     // 只为非 text 类型的配置项设置默认值
        //     if (item.type !== 'text') {
        //       defaultConfigValues[key] = item.default;
        //     }
        //   });
        // }
        // 更新 agentConfigValues 为默认值
        setAgentConfigValues(defaultConfigValues);
        // 只有在 agent 不同时才更新 selectedAgent
        if (currentSelectedAgent?.id !== clientId) {
          setSelectedAgent({
            id: clientId,
            name: clientName || agentData?.name || clientId,
            logo: clientLogo || agentData?.logo,
            description: agentData?.description,
            config: agentConfig,
          });
        }
      } catch (error) {
        // 即使加载失败，也设置基本的 agent 信息和空的配置值
        setAgentConfigValues({});
        setSelectedAgent({
          id: clientId,
          name: clientName || clientId,
          logo: clientLogo,
          config: [],
        });
      }
    },
    // 🔥 移除 selectedAgent 依赖，使用 store.getState() 获取最新值
    [setSelectedAgent, setAgentConfigValues]
  );
  /**
   * 加载会话列表
   */
  const loadConversations = useCallback(
    async (shareConversation?: string | null, force = false) => {
      // 检查 projectId 是否变化，如果变化则重置加载状态
      if (prevProjectIdRef.current !== projectId) {
        conversationsLoadedRef.current = false;
        prevProjectIdRef.current = projectId;
      }
      // 🔥 关键修复：使用 store 的 loadingStates 做全局级别的防重复检查
      // 这样即使在 React StrictMode 下也能正确防止重复请求
      const currentLoadingState =
        useConversationsStore.getState().loadingStates[loadingKey];
      if (currentLoadingState) {
        return;
      }
      if (!force && conversationsLoadedRef.current) {
        return;
      }
      loadingConversationsRef.current = true;
      setIsLoading(loadingKey, true);
      try {
        const data = await conversationService.fetchConversations(
          shareConversation,
          projectId || null
        );
        // 合并会话：保留其他 projectId 的会话，替换当前 projectId 的会话
        setConversations(prev => {
          // 防御性检查
          if (!Array.isArray(prev)) {
            console.warn(
              '⚠️ prev conversations is not an array, resetting to empty'
            );
            return data;
          }
          if (!Array.isArray(data)) {
            return prev;
          }
          // 过滤掉当前 projectId 的旧会话
          // IMPORTANT: 保留 temp- 开头的临时会话，它们是刚创建还未同步到后端的
          const otherProjectConvs = prev.filter(conv => {
            // 🔥 修复：只保留那些后端还没有创建的 temp- conversation
            // 如果后端返回的数据中有相同 title 和 projectId 的 conversation，
            // 说明这个 temp- conversation 已经被后端创建了，不需要保留
            if (conv.id.startsWith('temp-')) {
              const hasMatchingBackendConv = data.some(
                d => d.title === conv.title && d.projectId === conv.projectId
              );
              if (hasMatchingBackendConv) {
                return false; // 不保留，因为后端已经有了
              }
              return true; // 保留，可能还在创建中
            }
            // 🔥 保留群聊会话：因为 loadConversations 只获取单聊，不能覆盖群聊
            if (conv.conversationType === 'group') {
              return true;
            }
            // 🔥 保留正在加载的会话：避免 loadConversationDetails 和 loadConversations 的竞态条件
            // 场景：刷新浏览器时，loadConversationDetails 创建了 isLoading=true 的临时会话，
            // 但 loadConversations 可能先返回并过滤掉它
            // 🔥 修复：但如果后端数据中已有相同 ID 或相同 title+projectId 的会话，则不保留（避免重复）
            if (conv.isLoading) {
              const existsInBackendById = data.some(d => d.id === conv.id);
              const existsInBackendByTitle = data.some(
                d => d.title === conv.title && d.projectId === conv.projectId
              );
              if (existsInBackendById || existsInBackendByTitle) {
                return false; // 后端已有，不保留
              }
              return true;
            }
            // 如果加载的是通用会话 (projectId === null)，保留有 projectId 的会话
            if (projectId === null || projectId === undefined) {
              return conv.projectId !== null && conv.projectId !== undefined;
            }
            // 如果加载的是项目会话，保留其他 projectId 的会话（包括通用会话）
            return conv.projectId !== projectId;
          });
          // 合并新会话：如果旧数据中已有该 conversation 且有 messages，则保留 messages
          const mergedConversations = data.map(newConv => {
            const existingConv = prev.find(c => c.id === newConv.id);

            if (
              existingConv?.messages &&
              Array.isArray(existingConv.messages)
            ) {
              // 🔥 关键修复：始终保留本地 messages，即使长度为 0
              // 因为本地可能有前端创建的 user message 尚未同步到后端
              // 或者有临时的 assistant loading message
              const localMessageCount = existingConv.messages.length;
              const backendMessageCount = Array.isArray(newConv.messages)
                ? newConv.messages.length
                : 0;

              // 如果本地有更多 messages，或者本地有 messages 而后端没有，保留本地的
              if (
                localMessageCount > backendMessageCount ||
                (localMessageCount > 0 && backendMessageCount === 0)
              ) {
                return {
                  ...newConv,
                  messages: existingConv.messages,
                  treeData: existingConv.treeData || newConv.treeData,
                  versionPath: existingConv.versionPath || newConv.versionPath,
                  isLoading: existingConv.isLoading || false,
                };
              }
            }

            // 🔥 新增：即使后端有数据，如果本地已经加载了详细的 treeData，也要保留
            // 这解决了 loadConversationDetails 加载后被 loadConversations 覆盖的问题
            if (existingConv?.treeData && existingConv.treeData.length > 0) {
              return {
                ...newConv,
                messages: existingConv.messages || newConv.messages,
                treeData: existingConv.treeData,
                versionPath: existingConv.versionPath || newConv.versionPath,
                isLoading: existingConv.isLoading || false,
              };
            }

            return newConv;
          });
          // 合并到前面
          const allConvs = [...mergedConversations, ...otherProjectConvs];

          // 🔥 修复：去重，如果同一个 conversationId 出现多次，只保留第一个
          // 这解决了 temp- ID 转换为真实 ID 后，又从后端获取相同 ID 导致重复的问题
          const seenIds = new Set<string>();
          const dedupedConvs = allConvs.filter(conv => {
            if (seenIds.has(conv.id)) {
              return false;
            }
            seenIds.add(conv.id);
            return true;
          });

          return dedupedConvs;
        });
        conversationsLoadedRef.current = true;
        // Auto-select the shared conversation if exists
        if (shareConversation && data.length > 0) {
          const sharedConv = data.find(
            conv => conv.conversationId === shareConversation
          );
          if (sharedConv) {
            setCurrentConversationId(sharedConv.id);
          }
        }
        if (projectId) {
          setSidebarProjectConversationsCache(prev => {
            // 🔥 修复：保留当前 projectId 的 temp- conversations
            const existingTempConvs = (prev[projectId] || []).filter(conv =>
              conv.id.startsWith('temp-')
            );

            // 🔥 关键修复：如果有 temp- conversations，过滤掉后端刚创建的 conversations
            // 因为它们可能对应前端的 temp- conversation，等 message_start 来转换
            let filteredData = data;
            if (existingTempConvs.length > 0) {
              const now = Date.now();
              const RECENT_THRESHOLD = 10000; // 10秒内创建的认为是"新的"

              filteredData = data.filter(conv => {
                const createdAt = new Date(conv.createdAt).getTime();
                const isRecent = now - createdAt < RECENT_THRESHOLD;

                if (isRecent) {
                  return false; // 跳过最近创建的
                }
                return true;
              });
            }

            // 🔥 重要：对后端数据进行合并，保留本地已加载的详细数据
            const mergedFilteredData = filteredData.map(newConv => {
              const existingConv = (prev[projectId] || []).find(
                c => c.id === newConv.id
              );

              // 如果本地已有这个 conversation 且有详细数据，保留它
              if (existingConv) {
                // 保留本地的 messages、treeData、versionPath（如果存在）
                if (
                  (existingConv.messages && existingConv.messages.length > 0) ||
                  (existingConv.treeData && existingConv.treeData.length > 0)
                ) {
                  return {
                    ...newConv,
                    messages: existingConv.messages || newConv.messages,
                    treeData: existingConv.treeData || newConv.treeData,
                    versionPath:
                      existingConv.versionPath || newConv.versionPath,
                    isLoading: existingConv.isLoading || false,
                  };
                }
              }
              return newConv;
            });

            // 🔥 关键修复：保持前端的顺序，不要重新排列
            // 策略：
            // 1. 遍历 prev 中的对话，用 mergedFilteredData 更新匹配的项
            // 2. 只添加后端返回的新对话到末尾
            // 这样可以确保前端创建的对话位置不变（无论是 temp- 还是已转换）

            const prevConvs = prev[projectId] || [];
            const backendMap = new Map(
              mergedFilteredData.map(conv => [conv.id, conv])
            );

            // 更新 prev 中存在的对话，保持原顺序
            const updatedPrevConvs = prevConvs.map(prevConv => {
              const backendConv = backendMap.get(prevConv.id);
              if (backendConv) {
                // 用后端数据更新，但保持位置不变
                backendMap.delete(prevConv.id); // 标记为已处理
                return backendConv;
              }
              return prevConv;
            });

            // 找出后端返回的新对话（prev 中没有的）
            const newConvsFromBackend = Array.from(backendMap.values());

            // 合并：保留 prev 的顺序（已更新） + 新的后端对话
            const mergedData = [
              ...updatedPrevConvs, // 保留所有已存在的对话（按原顺序，但数据已更新）
              ...newConvsFromBackend, // 只添加新的后端对话
            ];

            // 🔥 修复：去重（理论上不应该有重复，但保险起见）
            const seenIds = new Set<string>();
            const dedupedData = mergedData.filter(conv => {
              if (seenIds.has(conv.id)) {
                return false;
              }
              seenIds.add(conv.id);
              return true;
            });

            return {
              ...prev,
              [projectId]: dedupedData,
            };
          });
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setIsLoading(loadingKey, false);
        loadingConversationsRef.current = false;
      }
    },
    [
      projectId,
      loadingKey,
      setSidebarProjectConversationsCache,
      setCurrentConversationId,
      setConversations,
      setIsLoading,
    ]
  );
  /**
   * 加载会话详情
   */
  const loadConversationDetails = useCallback(
    async (conversationId: string, force: boolean = false) => {
      // 如果正在加载这个会话的详情，则跳过
      if (loadingDetailsRef.current.has(conversationId)) {
        return;
      }
      loadingDetailsRef.current.add(conversationId);

      // 🔥 辅助函数：同时更新 conversations 和 sidebar cache 的 isLoading 状态
      const updateLoadingState = (convId: string, isLoading: boolean) => {
        setConversations(prev => {
          if (!Array.isArray(prev)) return prev;
          return prev.map(conv =>
            conv.id === convId ? { ...conv, isLoading } : conv
          );
        });
        // 同时更新 sidebar cache
        setSidebarProjectConversationsCache(prev => {
          const newCache = { ...prev };
          let updated = false;
          Object.keys(newCache).forEach(pid => {
            const projectConvs = newCache[pid] || [];
            const hasConv = projectConvs.some(conv => conv.id === convId);
            if (hasConv) {
              newCache[pid] = projectConvs.map(conv =>
                conv.id === convId ? { ...conv, isLoading } : conv
              );
              updated = true;
            }
          });
          return updated ? newCache : prev;
        });
      };

      // 检查conversation是否在列表中
      setConversations(prev => {
        if (!Array.isArray(prev)) {
          console.warn(
            '⚠️ prev conversations is not an array in loadConversationDetails'
          );
          return prev;
        }
        const exists = prev.find(conv => conv.id === conversationId);
        if (exists) {
          // 如果已经有消息了，且不是强制刷新，就不需要再加载了
          if (
            !force &&
            exists.messages &&
            Array.isArray(exists.messages) &&
            exists.messages.length > 0
          ) {
            loadingDetailsRef.current.delete(conversationId);
            return prev;
          }
          // 如果存在，设置loading状态（conversations store）
          // 🔥 同时更新 sidebar cache（在 setConversations 回调外部执行）
          setTimeout(() => updateLoadingState(conversationId, true), 0);
          return prev.map(conv =>
            conv.id === conversationId ? { ...conv, isLoading: true } : conv
          );
        } else {
          // 如果不存在，检查是否是群聊
          if (window.groupChatIds?.has(conversationId)) {
            // 🔥 修复：群聊也需要添加到 conversations store，以便发送消息时能获取 projectId
            // 从 window.groupConversations 获取群聊数据
            const groupConv = window.groupConversations?.find(
              (g: any) => g.id === conversationId
            );
            if (groupConv) {
              return [
                {
                  ...groupConv,
                  id: conversationId,
                  conversationId: conversationId,
                  treeData: groupConv.treeData || [],
                  versionPath: groupConv.versionPath || {},
                  isLoading: true,
                  conversationType: 'group', // 标记为群聊
                },
                ...prev,
              ];
            }
            // 如果没有找到群聊数据，创建一个临时对象
            return [
              {
                id: conversationId,
                conversationId: conversationId,
                title: 'Group Chat',
                treeData: [],
                versionPath: {},
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString(),
                isLoading: true,
                conversationType: 'group',
              },
              ...prev,
            ];
          }
          // 单聊：创建一个临时的conversation对象
          return [
            {
              id: conversationId,
              conversationId: conversationId,
              title: 'Loading...',
              treeData: [], // ✅ 初始化为空数组
              versionPath: {}, // ✅ 初始化为空对象
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              isLoading: true,
            },
            ...prev,
          ];
        }
      });
      try {
        const {
          treeData,
          versionPath,
          conversationType,
          title: apiTitle,
          projectId: apiProjectId,
        } = await conversationService.fetchConversationDetails(conversationId);

        // 🔥 如果是群聊，确保添加到 window.groupChatIds
        // 这修复了刷新浏览器时群聊未被识别的问题
        if (conversationType === 'group') {
          if (!window.groupChatIds) {
            window.groupChatIds = new Set();
          }
          window.groupChatIds.add(conversationId);
        }

        // ✅ 从 treeData 和 versionPath 构建 messages 数组（用于渲染和兼容性）
        const messages = conversationService.rebuildMessagesWithVersionPath(
          treeData,
          conversationId,
          versionPath
        );

        // 从 treeData 中找到最后一个有 client 的节点
        const lastClient = conversationService.getLastClientFromTreeData(
          treeData,
          versionPath
        );

        // 如果找到了 client，则加载对应的 agent 配置
        // 🚀 优化：只在 agent 改变时才强制重置配置值
        // 异步加载，不阻塞对话列表的渲染
        if (lastClient?.id) {
          // 🔥 性能优化：使用 store.getState() 获取最新 selectedAgent，避免闭包依赖
          const currentSelectedAgent = useUIStore.getState().selectedAgent;
          const shouldForceReset = currentSelectedAgent?.id !== lastClient.id;
          loadAgentConfig(
            lastClient.id,
            lastClient.name,
            lastClient.logo,
            shouldForceReset
          ).catch(_error => {
            // Ignore error
          });
        }

        // 更新会话数据
        setConversations(prev => {
          if (!Array.isArray(prev)) {
            console.warn(
              '⚠️ prev conversations is not an array when updating messages'
            );
            return prev;
          }
          return prev.map(conv => {
            if (conv.id === conversationId) {
              // 如果 title 是 'Loading...'，从 API 或 treeData 获取 title
              let title = conv.title;
              if (title === 'Loading...') {
                if (apiTitle) {
                  title = apiTitle;
                } else if (treeData.length > 0) {
                  const firstContent = treeData[0]?.contents?.[0]?.content;
                  title = firstContent?.slice(0, 50) || 'Conversation';
                }
              }

              return {
                ...conv,
                title,
                treeData: treeData, // ✅ 唯一数据源
                versionPath: versionPath, // ✅ 激活路径
                messages: messages, // ✅ 从 treeData 构建的 messages（用于兼容）
                // Preserve original updatedAt from initial fetch; backend detail endpoint does not supply updatedAt
                updatedAt: conv.updatedAt,
                isLoading: false,
                // 🔥 保存会话类型，用于判断群聊
                conversationType: conversationType || conv.conversationType,
                projectId: apiProjectId ?? conv.projectId,
              };
            }
            return conv;
          });
        });
        // 🔥 同时更新 sidebar cache
        updateLoadingState(conversationId, false);
      } catch (error) {
        // 清除loading状态
        setConversations(prev => {
          if (!Array.isArray(prev)) {
            return prev;
          }
          return prev.map(conv =>
            conv.id === conversationId ? { ...conv, isLoading: false } : conv
          );
        });
        // 🔥 同时更新 sidebar cache
        updateLoadingState(conversationId, false);
      } finally {
        loadingDetailsRef.current.delete(conversationId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setConversations, loadAgentConfig, setSidebarProjectConversationsCache]
  );
  /**
   * 选择会话
   */
  const selectConversation = useCallback(
    async (id: string) => {
      // 🔥 性能优化：检查 currentConversationId 是否已经是目标 ID
      // 如果是，跳过状态更新（hash router 已经设置过了）
      const storeState = useConversationsStore.getState();
      const alreadySelected = storeState.currentConversationId === id;

      // 检查是否正在处理同一个 conversation（通过 ref）
      // 这是为了防止在异步操作完成前重复调用
      if (
        id === targetConversationIdRef.current &&
        loadingDetailsRef.current.has(id)
      ) {
        return;
      }

      // 立即更新 ref，防止重复调用
      targetConversationIdRef.current = id;
      // 🔥 性能优化：使用 store.getState() 获取最新 conversations，避免将 conversations 作为依赖
      // 这样 selectConversation 不会因为 conversations 变化而重新创建
      const currentConversations = storeState.conversations;
      const conversation = Array.isArray(currentConversations)
        ? currentConversations.find(conv => conv.id === id)
        : undefined;
      // 对于 temp- 会话，不加载详情（因为它们是前端新创建的，后端还没有）
      // 直接设置为当前会话即可
      if (id.startsWith('temp-')) {
        if (!alreadySelected) {
          setCurrentConversationId(id);
        }
        return;
      }

      // 🚀 性能优化：只在必要时设置 currentConversationId
      // 如果 hash router 已经设置过，跳过重复设置
      if (!alreadySelected) {
        setCurrentConversationId(id);
      }

      if (
        !conversation ||
        !conversation?.messages ||
        !Array.isArray(conversation.messages) ||
        conversation?.messages?.length === 0
      ) {
        // 需要加载详情，设置 pending 状态显示 loading
        setPendingConversationId(id);
        try {
          // 加载详情（不阻塞 UI）
          await loadConversationDetails(id);
          // 🔥 如果是群聊，刷新侧边栏未读数（访问后 lastVisitedAt 会更新）
          if (window.groupChatIds?.has(id) && window.refreshSidebarGroupChats) {
            window.refreshSidebarGroupChats();
          }
        } catch (error) {
          // 加载失败时记录错误，但不清空选中状态
          console.error('Failed to load conversation details:', error);
        } finally {
          // 无论成功还是失败，都要清理pending状态，避免loading永久显示
          setPendingConversationId(undefined);
        }
      } else {
        // 已有消息，直接切换
        // 检查并修复loading状态不一致的问题
        // 场景：对话A在后台完成了，但用户当时在对话B，导致对话A的loading一直存在
        if (conversation.isLoading && conversation.messages.length > 0) {
          const lastMessage =
            conversation.messages[conversation.messages.length - 1];
          if (
            lastMessage &&
            lastMessage.role === 'assistant' &&
            lastMessage.isStreaming === false
          ) {
            // 更新conversation的isLoading状态
            setConversations(prev =>
              prev.map(conv =>
                conv.id === id ? { ...conv, isLoading: false } : conv
              )
            );
          }
        }

        // 🚀 优化：先立即切换对话，然后异步加载 agent 配置（不阻塞）
        setCurrentConversationId(id);

        // 🔥 如果是群聊，刷新侧边栏未读数（访问后 lastVisitedAt 会更新）
        if (window.groupChatIds?.has(id) && window.refreshSidebarGroupChats) {
          window.refreshSidebarGroupChats();
        }

        // 检查并更新 agent 配置（即使 conversation 已加载）
        // 只在 agent 真正改变时才重置配置值
        const messages = conversation.messages;
        const lastMessageWithClient = messages
          .slice()
          .reverse()
          .find(msg => msg.client?.id);
        if (lastMessageWithClient?.client) {
          const clientId = lastMessageWithClient.client.id;
          // 🚀 优化：只在 agent 改变时才强制重置配置值
          // 🔥 性能优化：使用 store.getState() 获取最新 selectedAgent
          const currentAgent = useUIStore.getState().selectedAgent;
          const shouldForceReset = currentAgent?.id !== clientId;

          // 🚀 优化：不 await，让它异步执行，不阻塞切换
          loadAgentConfig(
            clientId,
            lastMessageWithClient.client.name,
            lastMessageWithClient.client.logo,
            shouldForceReset
          ).catch(_error => {
            // Ignore error
          });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      // 🔥 移除 conversations 依赖，使用 store.getState() 获取最新值
      loadConversationDetails,
      setCurrentConversationId,
      loadAgentConfig,
      setConversations,
      setPendingConversationId,
    ]
  );
  /**
   * 创建新会话
   */
  const createNewConversation = useCallback(() => {
    targetConversationIdRef.current = undefined;
    setCurrentConversationId(undefined);
  }, [setCurrentConversationId]);
  /**
   * 更新会话
   * @returns { code: number } - 返回响应码，调用方可以判断是否成功
   */
  const updateConversation = useCallback(
    async (id: string, method: 'PUT' | 'DELETE', title?: string) => {
      // Find the conversation to determine its projectId
      const conversation = conversations.find(conv => conv.id === id);
      const conversationProjectId = conversation?.projectId;
      // 🔥 不再做乐观删除（Optimistic Delete），改为等 API 成功后再更新状态。
      // 原因：乐观删除 + 失败后恢复（loadConversations）会导致两次状态变化，
      // 触发 ConversationList 连续重渲染，使 DeleteConversationDialog 弹窗闪烁。
      // 删除操作已经有 Dialog 的 loading 状态作为用户反馈，不需要乐观更新。
      const res: any = await conversationService.updateConversation(
        id,
        method,
        title
      );
      // 检查响应码
      if (res?.code !== 200) {
        // 失败时不做任何状态变化，避免触发重渲染导致弹窗闪烁
        return res;
      }
      // 成功后才更新本地状态
      if (method === 'DELETE') {
        setConversations(prev => prev.filter(conv => conv.id !== id));

        // Also remove from sidebar cache if it's a project conversation
        if (conversationProjectId) {
          setSidebarProjectConversationsCache(prev => ({
            ...prev,
            [conversationProjectId]: (prev[conversationProjectId] || []).filter(
              conv => conv.id !== id
            ),
          }));
        }
      }
      // 重新加载会话列表 - 强制刷新
      conversationsLoadedRef.current = false;
      // If the conversation belongs to a project, reload project conversations
      if (conversationProjectId) {
        // Reload project conversations to update sidebar cache (async, don't wait)
        conversationService
          .fetchConversations(null, conversationProjectId)
          .then(data => {
            setSidebarProjectConversationsCache(prev => ({
              ...prev,
              [conversationProjectId]: data,
            }));
          });
      }
      // Reload conversations list for the current projectId (async, don't wait)
      loadConversations(undefined, true);
      return res;
    },
    [
      conversations,
      loadConversations,
      setConversations,
      setSidebarProjectConversationsCache,
    ]
  );
  /**
   * 更新会话中的消息
   *
   * 注意：临时消息使用 message.id 存储临时ID（格式: "temp-xxx"）
   * 通过 isPendingId(message.id) 判断是否为临时消息
   * 用于：
   * 1. 匹配流式更新的临时消息
   * 2. 在 Retry 时匹配预先添加的空 choice
   * 3. 停止特定请求
   */
  const updateConversationMessage = useCallback(
    (conversationId: string, message: Message) => {
      setConversations(prev => {
        if (!Array.isArray(prev)) {
          console.warn(
            '⚠️ prev conversations is not an array in updateConversationMessage'
          );
          return prev;
        }

        // 检查 conversation 是否有 treeData
        // 如果有，这意味着会有双路径更新：messages + treeData
        // 这是正常的，因为 TreeMessageRenderer 读取 treeData，而某些逻辑依赖 messages

        const result = prev.map(conversation => {
          if (conversation.id === conversationId) {
            const messages = conversation.messages || [];
            // 首先通过 id 查找消息
            let existingMessageIndex = messages.findIndex(
              msg => msg.id === message.id
            );
            // 🔥 重构：如果通过 id 找不到，尝试通过临时消息ID匹配
            // 扩展到 user 消息，用于更新 waiting -> ongoing 状态
            if (existingMessageIndex < 0 && isPendingId(message.id)) {
              existingMessageIndex = messages.findIndex(
                msg =>
                  msg.role === message.role &&
                  String(msg.id) === String(message.id)
              );
            }
            // 🔥 修复问题3：对于 _isStatusOnlyUpdate 消息，如果仍然找不到，尝试通过 backendId 匹配
            // 这解决了 userStatusUpdate 使用不同 ID 格式（user-status-xxx）导致无法匹配的问题
            if (
              existingMessageIndex < 0 &&
              (message as any)._isStatusOnlyUpdate &&
              message.backendId
            ) {
              existingMessageIndex = messages.findIndex(
                msg =>
                  msg.role === message.role &&
                  msg.backendId === message.backendId
              );
            }
            // 🔥 修复：通用 backendId 匹配（防止 SSE 和 WS 因 ID 类型不同创建重复消息）
            // SSE 使用数字 id（如 123），WS 使用字符串 id（如 "123"），直接匹配失败
            // 通过 backendId 匹配可以找到 WS 已创建的消息并更新而非重复添加
            if (existingMessageIndex < 0 && message.backendId) {
              existingMessageIndex = messages.findIndex(
                msg =>
                  msg.role === message.role &&
                  msg.backendId === message.backendId
              );
            }
            let updatedMessages;

            // 状态更新专用处理（只更新 messageStatus，不修改其他内容）
            // 用于 message_start 时更新 user 消息状态从 waiting 变为 ongoing
            if (
              (message as any)._isStatusOnlyUpdate &&
              existingMessageIndex >= 0
            ) {
              const existingMsg = messages[existingMessageIndex];
              updatedMessages = messages.map((msg, index) =>
                index === existingMessageIndex
                  ? {
                      ...existingMsg,
                      messageStatus: (message as any).messageStatus,
                      // 🔥 保留原消息的 isCanceled 属性
                      isCanceled: existingMsg.isCanceled,
                    }
                  : msg
              );
            }
            // Case 1: 流式结束时（isStreaming = false）
            // 🔥 重构：使用 isPendingId(message.id) 判断临时消息
            else if (
              message.role === 'assistant' &&
              message.isStreaming === false &&
              isPendingId(message.id) &&
              existingMessageIndex >= 0
            ) {
              const existingMsg = messages[existingMessageIndex];
              // 检查后端是否返回了choices（包含version信息）
              if (message.choices && message.choices.length > 0) {
                // 检查前端是否已有多个choices（Retry预先添加的）
                if (existingMsg.choices && existingMsg.choices.length > 1) {
                  // 前端有多个choices，需要合并而不是替换
                  // 策略：用后端返回的choices更新version，保留前端的旧choices
                  let finalChoices;
                  if (message.choices.length === existingMsg.choices.length) {
                    // 后端返回的数量和前端一致，直接使用后端的（包含正确的version和完整内容）
                    finalChoices = message.choices;
                  } else if (
                    message.choices.length > existingMsg.choices.length
                  ) {
                    // 后端返回的更多（不太可能），使用后端的
                    finalChoices = message.choices;
                  } else {
                    // 后端返回的少（最常见情况：后端只返回最新的1个）
                    // 用后端的第一个choice更新前端的第一个choice，保留前端的其他choices
                    const backendChoices = message.choices;

                    // 🔧 修复：检查后端返回的第一个choice的blocks是否为空
                    // 如果为空，使用前端在message_block中累积的blocks
                    const firstBackendChoice = backendChoices[0];
                    const shouldUseBackendBlocks =
                      firstBackendChoice.blocks &&
                      firstBackendChoice.blocks.length > 0;

                    if (shouldUseBackendBlocks) {
                      // 后端有内容，使用后端的
                      const frontendOldChoices = existingMsg.choices.slice(
                        backendChoices.length
                      );
                      finalChoices = [...backendChoices, ...frontendOldChoices];
                    } else {
                      // 后端的blocks为空，使用前端累积的blocks
                      const updatedFirstChoice = {
                        ...firstBackendChoice,
                        blocks: existingMsg.choices[0].blocks, // 使用前端累积的blocks
                      };
                      const frontendOldChoices = existingMsg.choices.slice(1);
                      finalChoices = [
                        updatedFirstChoice,
                        ...frontendOldChoices,
                      ];
                    }
                  }
                  // 更新assistant消息和user消息
                  updatedMessages = messages.map((msg, index) => {
                    if (index === existingMessageIndex) {
                      // 更新assistant消息，保留原ID
                      return {
                        ...existingMsg, // 保留原消息属性
                        ...message, // 使用流式消息的新内容
                        id: existingMsg.id, // 保留原消息的ID
                        choices: finalChoices,
                        // 🔥 保留原消息的 isCanceled 属性
                        isCanceled: existingMsg.isCanceled,
                      };
                    } else if (
                      msg.role === 'user' &&
                      String(msg.id) === String(message.id) // 🔥 重构：使用 id 匹配
                    ) {
                      // 同步user消息的version
                      const userChoices =
                        msg.choices?.map((choice, idx) => {
                          const backendVersion = finalChoices?.[idx]?.version;
                          if (backendVersion !== undefined) {
                            return { ...choice, version: backendVersion };
                          }
                          return choice;
                        }) || msg.choices;
                      return {
                        ...msg,
                        choices: userChoices,
                      };
                    }
                    return msg;
                  });
                } else {
                  // 前端只有1个choice，正常使用后端返回的choices
                  updatedMessages = messages.map((msg, index) => {
                    if (index === existingMessageIndex) {
                      return {
                        ...existingMsg, // 保留原消息属性
                        ...message, // 使用流式消息的新内容
                        id: existingMsg.id, // 保留原消息的ID
                        // 🔥 保留原消息的 isCanceled 属性
                        isCanceled: existingMsg.isCanceled,
                      };
                    } else if (
                      msg.role === 'user' &&
                      String(msg.id) === String(message.id) // 🔥 重构：使用 id 匹配
                    ) {
                      const userChoices =
                        msg.choices?.map((choice, idx) => {
                          const backendVersion =
                            message.choices?.[idx]?.version;
                          if (backendVersion !== undefined) {
                            return { ...choice, version: backendVersion };
                          }
                          return choice;
                        }) || msg.choices;
                      return {
                        ...msg,
                        choices: userChoices,
                      };
                    }
                    return msg;
                  });
                }
              } else {
                // 后端没有返回choices，保留前端的choices
                if (existingMsg.choices && existingMsg.choices.length > 1) {
                  // 更新第一个choice的内容
                  const updatedChoices = existingMsg.choices.map(
                    (choice, idx) =>
                      idx === 0
                        ? {
                            ...choice,
                            blocks: message.choices?.[0]?.blocks || [],
                          }
                        : choice
                  );
                  updatedMessages = messages.map((msg, index) =>
                    index === existingMessageIndex
                      ? {
                          ...existingMsg, // 保留原消息属性
                          ...message, // 使用流式消息的新内容
                          id: existingMsg.id, // 保留原消息的ID
                          choices: updatedChoices,
                          // 🔥 保留原消息的 isCanceled 属性
                          isCanceled: existingMsg.isCanceled,
                        }
                      : msg
                  );
                } else {
                  // 正常更新
                  updatedMessages = messages.map((msg, index) =>
                    index === existingMessageIndex
                      ? {
                          ...existingMsg, // 保留原消息属性
                          ...message, // 使用流式消息的新内容
                          id: existingMsg.id, // 保留原消息的ID
                          // 🔥 保留原消息的 isCanceled 属性
                          isCanceled: existingMsg.isCanceled,
                        }
                      : msg
                  );
                }
              }
            }
            // Case 2: 正常的流式更新或新消息
            else if (existingMessageIndex >= 0) {
              // Update existing message (streaming update)
              // 保留多个choices（如果有的话），只更新第一个choice的blocks
              const existingMsg = messages[existingMessageIndex];
              updatedMessages = messages.map((msg, index) => {
                if (index === existingMessageIndex) {
                  if (existingMsg.choices && existingMsg.choices.length > 1) {
                    const updatedChoices = existingMsg.choices.map(
                      (choice, idx) =>
                        idx === 0
                          ? {
                              ...choice,
                              blocks: message.choices?.[0]?.blocks || [],
                            }
                          : choice
                    );
                    return {
                      ...existingMsg, // 保留原消息的属性（包括原ID）
                      ...message, // 使用流式消息的新内容
                      id: existingMsg.id, // 保留原消息的ID
                      client: message.client
                        ? {
                            ...message.client,
                            logo:
                              message.client.logo || existingMsg.client?.logo,
                          }
                        : existingMsg.client, // 保留 client 信息，合并 logo
                      choices: updatedChoices,
                      // 🔥 保留原消息的 isCanceled 属性
                      isCanceled: existingMsg.isCanceled,
                    };
                  }
                  // 单个choice的情况，也要保留原ID，并更新 choices[0].blocks
                  const updatedChoices = existingMsg.choices
                    ? existingMsg.choices.map((choice, idx) =>
                        idx === 0
                          ? {
                              ...choice,
                              blocks: message.choices?.[0]?.blocks || [],
                            }
                          : choice
                      )
                    : [
                        {
                          version: 0,
                          blocks: message.choices?.[0]?.blocks || [],
                        },
                      ];

                  return {
                    ...existingMsg, // 保留原消息的属性
                    ...message, // 使用流式消息的新内容
                    id: existingMsg.id, // 保留原消息的ID
                    client: message.client
                      ? {
                          ...message.client,
                          logo: message.client.logo || existingMsg.client?.logo,
                        }
                      : existingMsg.client, // 保留 client 信息，合并 logo
                    choices: updatedChoices, // ✅ 关键修复：更新 choices[0].blocks
                    // 🔥 保留原消息的 isCanceled 属性
                    isCanceled: existingMsg.isCanceled,
                  };
                }
                return msg;
              });
            } else {
              // Add new message
              // 🔥 重构：检查是否已有相同临时ID的assistant消息（点击Retry时前端预先添加的）
              // 或者是否有空blocks的临时loading消息（新发送时前端预先添加的）
              if (message.role === 'assistant' && isPendingId(message.id)) {
                const existingAssistantIndex = messages.findIndex(
                  msg =>
                    msg.role === 'assistant' &&
                    (String(msg.id) === String(message.id) || // retry情况
                      (msg.isStreaming &&
                        msg.choices?.[0]?.blocks &&
                        msg.choices[0].blocks.length === 0)) // 新消息的临时loading
                );
                if (existingAssistantIndex >= 0) {
                  const existingAssistant = messages[existingAssistantIndex];
                  // 检查是否有多个choices（说明是Retry时前端预先添加的）
                  if (
                    existingAssistant.choices &&
                    existingAssistant.choices.length > 1
                  ) {
                    // 用新消息替换旧消息，保留旧的choices（除了第一个）
                    updatedMessages = messages.map((msg, idx) => {
                      if (idx === existingAssistantIndex) {
                        // 创建新的第一个choice
                        const newFirstChoice = {
                          version: existingAssistant.choices[0].version,
                          blocks: message.choices?.[0]?.blocks || [],
                        };
                        // 保留旧的choices（从第二个开始）
                        const oldChoices = existingAssistant.choices.slice(1);
                        return {
                          ...message, // 使用新消息（包括新id）
                          client: message.client
                            ? {
                                ...message.client,
                                logo:
                                  message.client.logo ||
                                  existingAssistant.client?.logo,
                              }
                            : existingAssistant.client, // 保留 client 信息，合并 logo
                          choices: [newFirstChoice, ...oldChoices],
                        };
                      }
                      return msg;
                    });
                  } else {
                    // 有旧消息但只有1个choice（或者是临时loading消息），正常替换
                    updatedMessages = messages.map((msg, idx) =>
                      idx === existingAssistantIndex
                        ? {
                            ...message,
                            client: message.client
                              ? {
                                  ...message.client,
                                  logo: message.client.logo || msg.client?.logo,
                                }
                              : msg.client, // 保留 client 信息，合并 logo
                          }
                        : msg
                    );
                  }
                } else {
                  // 没有旧消息，正常添加
                  updatedMessages = [...messages, message];
                }
              } else {
                // 非assistant消息，正常添加
                updatedMessages = [...messages, message];
              }
            }
            // 🔥 修复：只有 assistant 消息结束流式传输时才将 isLoading 设为 false
            // 用户消息不应该影响 isLoading 状态
            // 同时检查 messageStatus：如果是 waiting 或 ongoing，保持 isLoading 为 true
            // 🔥 新增：还需检查是否有其他活跃的流式消息或等待中的任务
            const hasActiveStreaming = useUIStore
              .getState()
              .hasActiveStreamingMessages(conversationId);
            const { taskQueue } = useUIStore.getState();
            const hasPendingTasks = taskQueue.some(
              task =>
                task.conversationId === conversationId &&
                [
                  'pending',
                  'sent',
                  'initialized',
                  'streaming',
                  'waiting',
                ].includes(task.status)
            );

            const shouldSetLoadingFalse =
              message.role === 'assistant' &&
              message.isStreaming === false &&
              (message as any).messageStatus !== 'waiting' &&
              (message as any).messageStatus !== 'ongoing' &&
              !hasActiveStreaming &&
              !hasPendingTasks;

            // 🔥 修复：当 message_init 返回真正的 conversationId 时，更新 conversation 的 conversationId
            // 这样后续消息发送时使用正确的后端 ID，避免消息串台或丢失
            const messageConversationId = (message as any).conversationId;
            const shouldUpdateConversationId =
              messageConversationId &&
              typeof messageConversationId === 'string' &&
              !messageConversationId.startsWith('temp-') &&
              (conversation.conversationId?.startsWith('temp-') ||
                !conversation.conversationId ||
                conversation.conversationId === conversation.id);

            const updatedConv = {
              ...conversation,
              messages: updatedMessages,
              updatedAt: new Date().toISOString(),
              isLoading: shouldSetLoadingFalse ? false : conversation.isLoading,
              // 🔥 新增：更新 conversationId 为后端返回的真实 ID
              ...(shouldUpdateConversationId && {
                conversationId: messageConversationId,
              }),
            };

            return updatedConv;
          }
          return conversation;
        });

        // 🔥 修复：不排序，保持创建时的顺序
        // 排序会导致侧边栏在对话进行时不断跳动
        return result;
      });

      // 🔥 修复：同时更新 sidebar cache 中对应的 conversation 的 isLoading 状态
      // 保持创建时的顺序，避免侧边栏跳动
      // 🔥 同步修复：与上面的逻辑一致，只有 assistant 消息结束时才设置 isLoading 为 false
      // 🔥 新增：还需检查是否有其他活跃的流式消息或等待中的任务
      const sidebarHasActiveStreaming = useUIStore
        .getState()
        .hasActiveStreamingMessages(conversationId);
      const { taskQueue: sidebarTaskQueue } = useUIStore.getState();
      const sidebarHasPendingTasks = sidebarTaskQueue.some(
        task =>
          task.conversationId === conversationId &&
          ['pending', 'sent', 'initialized', 'streaming', 'waiting'].includes(
            task.status
          )
      );

      const shouldSetSidebarLoadingFalse =
        message.role === 'assistant' &&
        message.isStreaming === false &&
        (message as any).messageStatus !== 'waiting' &&
        (message as any).messageStatus !== 'ongoing' &&
        !sidebarHasActiveStreaming &&
        !sidebarHasPendingTasks;

      setSidebarProjectConversationsCache(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(projectId => {
          const convs = newCache[projectId] || [];
          const index = convs.findIndex(c => c.id === conversationId);
          if (index >= 0) {
            // 找到了，更新它（包括 isLoading 状态，但不改变位置）
            const updated = {
              ...convs[index],
              updatedAt: new Date().toISOString(),
              isLoading: shouldSetSidebarLoadingFalse
                ? false
                : convs[index].isLoading,
            };
            newCache[projectId] = [
              ...convs.slice(0, index),
              updated,
              ...convs.slice(index + 1),
            ];
            // 不排序，保持原有顺序
          }
        });
        return newCache;
      });
    },
    [setConversations, setSidebarProjectConversationsCache]
  );
  /**
   * 删除会话中的消息
   * @param conversationId 对话ID
   * @param messageId 消息的后端ID（backendId）
   */
  const deleteConversationSession = useCallback(
    async (conversationId: string, messageId: string) => {
      await conversationService.deleteConversationSession(
        conversationId,
        messageId
      );
      // 🔥 修复：删除消息后必须强制重新加载会话详情
      // 否则 loadConversationDetails 会因为缓存检查而跳过加载，导致 treeData 和 messages 仍是删除前的状态
      // 从而导致新消息的 previousMessageId 引用已删除的消息，触发 "Message xxx not found" 错误
      await loadConversationDetails(conversationId, true);
    },
    [loadConversationDetails]
  );
  // 获取当前会话
  const currentConversation = useMemo(() => {
    if (!Array.isArray(conversations)) {
      console.warn(
        '⚠️ conversations is not an array in currentConversation lookup'
      );
      return null;
    }
    // 🔥 使用 String() 转换确保 ID 比较类型一致
    const innerConversation = conversations.find(
      conv => String(conv.id) === String(currentConversationId)
    );
    return innerConversation || null;
  }, [conversations, currentConversationId]);
  /**
   * 强制刷新会话列表
   */
  const refreshConversations = useCallback(() => {
    conversationsLoadedRef.current = false;
    return loadConversations(undefined, true);
  }, [loadConversations]);
  return {
    conversations,
    currentConversation,
    currentConversationId,
    setCurrentConversationId,
    pendingConversationId,
    isLoading,
    expandedProjectIds,
    sidebarProjectConversationsCache,
    loadConversations,
    projectExpand,
    loadConversationDetails,
    selectConversation,
    createNewConversation,
    updateConversation,
    updateConversationMessage,
    deleteConversationSession,
    setConversations,
    refreshConversations,
    addMessageToConversation,
  };
}
